import type { CSSProperties, ReactNode } from "react";
import { useMemo } from "react";

const DATE_TIME_FORMATTER_DE = new Intl.DateTimeFormat("de-DE", {
  day: "2-digit",
  month: "2-digit",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  second: "2-digit"
});

const DATE_FIELD_KEY_PATTERN = /(createdat|updatedat|lastmodified|timestamp|letzte_aenderung|votereceivedat)/i;

const tryFormatDateTimeDE = (value: unknown) => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = new Date(trimmed);
  if (Number.isNaN(parsed.getTime())) return null;
  return DATE_TIME_FORMATTER_DE.format(parsed);
};

const renderDefaultCellValue = <T extends Record<string, unknown>>(row: T, columnKey: keyof T | string) => {
  const raw = row[columnKey as keyof T];
  const key = String(columnKey ?? "");
  if (DATE_FIELD_KEY_PATTERN.test(key)) {
    const formatted = tryFormatDateTimeDE(raw);
    if (formatted) return formatted;
  }
  return String(raw ?? "");
};

const ActionColumnIcon = () => (
  <span className="table-actions-head-icon" aria-hidden="true">
    <svg viewBox="0 0 20 20" fill="none">
      <circle cx="10" cy="5.3" r="1.4" fill="currentColor" />
      <circle cx="10" cy="10" r="1.4" fill="currentColor" />
      <circle cx="10" cy="14.7" r="1.4" fill="currentColor" />
    </svg>
  </span>
);

const isActionColumnKey = (key: PropertyKey) =>
  String(key ?? "").toLowerCase() === "actions";

type Props<T extends Record<string, unknown>> = {
  columns: Array<{
    key: keyof T | string;
    label: ReactNode;
    render?: (row: T) => ReactNode;
    align?: CSSProperties["textAlign"];
    width?: number | string;
  }>;
  rows: T[];
  total: number;
  page: number;
  pageSize: number;
  onPageChange: (next: number) => void;
  bodyMaxHeight?: number | string;
  topContent?: ReactNode;
};

export function ServerDataTable<T extends Record<string, unknown>>({
  columns,
  rows,
  total,
  page,
  pageSize,
  onPageChange,
  bodyMaxHeight = 420,
  topContent
}: Props<T>) {
  const totalPages = useMemo(() => Math.max(1, Math.ceil(total / pageSize)), [total, pageSize]);

  return (
    <div className="panel">
      {topContent ? <div style={{ marginBottom: 10 }}>{topContent}</div> : null}
      <div style={{ marginBottom: 8, fontSize: 12, color: "#5a6875" }}>
        Zeilen: {rows.length} / Gesamt: {total}
      </div>
      <div style={{ overflowX: "auto", overflowY: "visible", maxHeight: bodyMaxHeight }}>
        <table>
          <thead>
            <tr>
              {columns.map((c) => {
                const isActionColumn = isActionColumnKey(c.key);
                return (
                <th
                  key={String(c.key)}
                  className={isActionColumn ? "table-actions-head-cell" : undefined}
                  style={{
                    textAlign: isActionColumn ? "center" : c.align ?? "left",
                    width: isActionColumn ? c.width ?? 54 : c.width
                  }}
                  aria-label={isActionColumn ? "Aktionen" : undefined}
                  title={isActionColumn ? "Aktionen" : undefined}
                >
                  {isActionColumn ? <ActionColumnIcon /> : c.label}
                </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr key={index}>
                {columns.map((c) => {
                  const isActionColumn = isActionColumnKey(c.key);
                  return (
                  <td
                    key={String(c.key)}
                    className={isActionColumn ? "table-actions-cell" : undefined}
                    style={{
                      textAlign: isActionColumn ? "center" : c.align ?? "left",
                      width: isActionColumn ? c.width ?? 54 : c.width
                    }}
                  >
                    {isActionColumn ? (
                      <div className="table-actions-cell-axis">
                        {c.render ? c.render(row) : renderDefaultCellValue(row, c.key)}
                      </div>
                    ) : (
                      c.render ? c.render(row) : renderDefaultCellValue(row, c.key)
                    )}
                  </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div style={{ marginTop: 10, display: "flex", justifyContent: "space-between" }}>
        <button onClick={() => onPageChange(Math.max(1, page - 1))} disabled={page <= 1}>
          Zurück
        </button>
        <span style={{ fontSize: 13 }}>
          Seite {page} / {totalPages}
        </span>
        <button onClick={() => onPageChange(Math.min(totalPages, page + 1))} disabled={page >= totalPages}>
          Weiter
        </button>
      </div>
    </div>
  );
}
